/**
 * AuthButtons component implemented with Material UI
 *
 * This component provides a Material UI implementation of the AuthButtons component
 * with login/logout functionality.
 */
import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, Typography, CircularProgress, Theme, Tooltip, IconButton } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { AuthButtonsProps } from 'types/components/atoms';
import { UserProfile } from 'types/contexts/auth';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getErrorState } from 'reduxModules/ducks/ui';
import ReducersIndex from 'reduxModules/ducks';

/**
 * AuthButtons component
 *
 * @param props - Component props
 * @returns AuthButtons component
 */
const AuthButtons: React.FC<AuthButtonsProps> = (props) => {
  const {
    showLoginText = true,
    showUserName = true,
    variant = 'contained',
    ...rest
  } = props;

  const { user, loading, signin, signout, getUserProfile } = useAuth();
  const [userName, setUserName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Use refs to prevent multiple login/logout actions
  const isAuthenticating = useRef<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Check for error state - check if any errors exist in the error object
  const errors = useSelector((state: any) => state.ui?.error || {});
  const hasErrors = Object.keys(errors).length > 0;
  
  // Debug logging
  useEffect(() => {
    console.log('AuthButtons - Errors object:', errors);
    console.log('AuthButtons - Has errors:', hasErrors);
  }, [errors, hasErrors]);

  useEffect(() => {
    const fetchUserProfile = async (): Promise<void> => {
      if (user) {
        try {
          const profile: UserProfile = await getUserProfile();
          setUserName(profile?.name || profile?.email || 'User');
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setUserName('User');
        }
      }
      setIsLoading(false);
    };

    fetchUserProfile();
  }, [user, getUserProfile]);

  const handleSignIn = (): void => {
    if (isAuthenticating.current) return;

    isAuthenticating.current = true;
    console.log('Initiating sign in from button');

    // Reset the flag after a delay
    setTimeout(() => {
      isAuthenticating.current = false;
    }, 2000);

    signin();
  };

  const handleLogout = (): void => {
    if (isAuthenticating.current) return;

    isAuthenticating.current = true;
    console.log('Initiating logout from button');

    // Reset the flag after a delay
    setTimeout(() => {
      isAuthenticating.current = false;
    }, 2000);

    signout();
    navigate('/signin');
  };

  if (loading || isLoading) {
    return (
      <LoadingContainer {...rest}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        <Typography variant="body2" color="textSecondary">
          Loading...
        </Typography>
      </LoadingContainer>
    );
  }

  return (
    <Container {...rest}>
      {user ? (
        <>
          {hasErrors && (
            <Tooltip title="View warnings">
              <IconButton
                size="small"
                onClick={() => {
                  dispatch(ReducersIndex.uiReducers.openModal('warning'));
                }}
                sx={{
                  mr: 1,
                  color: '#ff9800',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 152, 0, 0.08)'
                  }
                }}
              >
                <WarningIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {showUserName && (
            <UserInfo variant="body2">
              Welcome, {userName}
            </UserInfo>
          )}
          <StyledButton
            variant={variant}
            color="primary"
            onClick={handleLogout}
            size="small"
          >
            {showLoginText ? 'Sign out' : ''}
          </StyledButton>
        </>
      ) : (
        <StyledButton
          variant={variant}
          color="primary"
          onClick={handleSignIn}
          size="small"
        >
          {showLoginText ? 'Sign in' : ''}
        </StyledButton>
      )}
    </Container>
  );
};

// Styled components using MUI styled API
const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginRight: '20px',
}));

const UserInfo = styled(Typography)(({ theme }) => ({
  marginRight: '10px',
  fontSize: '0.9rem',
  color: theme.palette.text.primary || '#333',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'var(--app-red, #e31937)',
  color: 'white',
  borderRadius: '4px',
  padding: '6px 12px',
  fontSize: '0.9rem',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#c01730',
  },
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginRight: '20px',
  fontSize: '0.9rem',
  color: theme.palette.text.secondary || '#666',
}));

export type { AuthButtonsProps };
export default AuthButtons;
